import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getStudent } from '../../services/studentService';
import {
  getEligibilityChecklist,
  listEligibilityChecklists,
  createEligibilityChecklist,
  updateResponses,
  updateHeader,
  syncChecklist,
  evaluateFromDocs,
  deleteChecklist,
  exportPdf,
  exportExcel,
  getTemplates,
  getTemplate,
  deleteTemplate,
} from '../../services/eligibilityService';
import { getFileView } from '../../services/fileCabinetService';
import ConfirmModal from '../common/ConfirmModal';
import Loader from '../common/Loader';
import Alert from '../common/Alert';
import DocumentViewer from '../common/DocumentViewer';
import DatePicker from '../common/DatePicker';
import ChecklistSectionPopover from './ChecklistSectionPopover';
import { complianceKeysForEligibilitySection } from '../../utils/checklistSectionMapping';
import {
  coerceSentComplete,
  formatChecklistDate,
  resolveChecklistDob,
  normalizeChecklistResponses,
} from '../../utils/checklistFormatting';

const B = 'border border-slate-300 dark:border-slate-600';
const CELL = `${B} px-3 py-2 text-sm text-gray-900 dark:text-slate-200 align-middle`;
const HCELL = `${B} px-3 py-2 text-xs font-bold uppercase tracking-wide text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700`;
const SECTION_TITLE = `${B} px-3 py-2.5 text-sm font-semibold text-indigo-800 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/30`;

const TAB_ORDER = [
  { id: null, label: 'Master List' },
  { id: 'autism', label: 'Autism', criteria: 'autism_criteria' },
  { id: 'developmental_delay', label: 'Developmental Delay', criteria: 'dd_criteria' },
  { id: 'hard_of_hearing', label: 'Hard of Hearing', criteria: 'hoh_criteria' },
  { id: 'intellectual_disability', label: 'Intellectual Disability', criteria: 'id_criteria' },
  { id: 'ohi_oi', label: 'OHI / OI', criteria: 'ohi_oi_criteria' },
  { id: 'specific_learning_disability', label: 'SLD', criteria: 'sld_criteria' },
  { id: 'speech_language', label: 'Speech Language', criteria: 'speech_criteria' },
  { id: 'visual_impairment', label: 'Visual Impairment', criteria: null },
];

function deepClone(obj) { return JSON.parse(JSON.stringify(obj)); }

/** API/cache payloads are not always arrays — never call .filter/.map without this. */
function coerceArray(value) {
  if (Array.isArray(value)) return value;
  if (value && Array.isArray(value.templates)) return value.templates;
  if (value && Array.isArray(value.items)) return value.items;
  if (value && Array.isArray(value.checklists)) return value.checklists;
  return [];
}

function CB({ checked }) {
  if (checked === true) return <span className="text-green-600 dark:text-green-400 font-medium">☑</span>;
  if (checked === 'N/A') return <span className="text-gray-400 dark:text-slate-500 font-medium">N/A</span>;
  return <span className="text-slate-400 dark:text-slate-500">☐</span>;
}


function YesNoCell({ value, editing, onChange }) {
  if (editing) {
    return (
      <div className="flex items-center gap-3">
        <label className="inline-flex items-center gap-1 cursor-pointer">
          <input type="radio" name={`yn_${Math.random()}`} checked={value === true} onChange={() => onChange(true)} className="w-3.5 h-3.5 text-green-600" />
          <span className="text-xs">Yes</span>
        </label>
        <label className="inline-flex items-center gap-1 cursor-pointer">
          <input type="radio" name={`yn_${Math.random()}`} checked={value === false} onChange={() => onChange(false)} className="w-3.5 h-3.5 text-red-600" />
          <span className="text-xs">No</span>
        </label>
      </div>
    );
  }
  if (value === true) return <span className="text-green-600 font-medium text-sm">Yes</span>;
  if (value === false) return <span className="text-red-600 font-medium text-sm">No</span>;
  return <span className="text-slate-400 text-sm">—</span>;
}

function YesNoNaCell({ value, editing, onChange }) {
  if (editing) {
    return (
      <div className="flex items-center gap-2">
        <label className="inline-flex items-center gap-1 cursor-pointer">
          <input type="radio" checked={value === true} onChange={() => onChange(true)} className="w-3.5 h-3.5 text-green-600" />
          <span className="text-xs">Yes</span>
        </label>
        <label className="inline-flex items-center gap-1 cursor-pointer">
          <input type="radio" checked={value === false} onChange={() => onChange(false)} className="w-3.5 h-3.5 text-red-600" />
          <span className="text-xs">No</span>
        </label>
        <label className="inline-flex items-center gap-1 cursor-pointer">
          <input type="radio" checked={value === 'N/A'} onChange={() => onChange('N/A')} className="w-3.5 h-3.5 text-gray-500" />
          <span className="text-xs">N/A</span>
        </label>
      </div>
    );
  }
  if (value === true) return <span className="text-green-600 font-medium text-sm">Yes</span>;
  if (value === false) return <span className="text-red-600 font-medium text-sm">No</span>;
  if (value === 'N/A') return <span className="text-gray-500 font-medium text-sm">N/A</span>;
  return <span className="text-slate-400 text-sm">—</span>;
}

function NotesHint({ notes }) {
  if (!notes || (Array.isArray(notes) && notes.length === 0)) return null;
  const text = Array.isArray(notes) ? notes.filter(Boolean).join(', ') : String(notes);
  if (!text.trim()) return null;
  return <span className="block text-[10px] text-gray-400 dark:text-slate-500 italic mt-0.5 leading-tight">{text}</span>;
}

function SourcePanel({ sectionDetail, onOpenFileAtPage }) {
  if (!sectionDetail?.source?.files?.length) return null;
  return (
    <div className="mt-2 space-y-1.5">
      {sectionDetail.source.files.map((file, i) => {
        const fileId = file.student_file_id || file.file_id;
        const pages = file.pages || [];
        const pageNums = file.page_numbers || [];
        return (
          <div key={i}>
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-slate-400">
              <span>📄</span>
              {fileId && onOpenFileAtPage ? (
                <button type="button" onClick={() => onOpenFileAtPage(fileId, null, file.file_name)} className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium">
                  {file.file_name}
                </button>
              ) : (
                <span>{file.file_name}</span>
              )}
            </div>
            {(pages.length > 0 || pageNums.length > 0) && (
              <div className="flex items-center gap-1 mt-0.5 ml-5 flex-wrap">
                {pages.length > 0 ? pages.map((pg, pi) => (
                  <button
                    key={pi}
                    type="button"
                    onClick={() => onOpenFileAtPage && onOpenFileAtPage(pg.student_file_id || fileId, pg.page, file.file_name)}
                    className="inline-flex items-center px-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 text-[10px] font-medium hover:bg-indigo-200 dark:hover:bg-indigo-800/40 transition-colors cursor-pointer"
                    title={`${file.file_name} — p. ${pg.page}`}
                  >
                    p. {pg.page}
                  </button>
                )) : pageNums.map((pn, pi) => (
                  <button
                    key={pi}
                    type="button"
                    onClick={() => fileId && onOpenFileAtPage && onOpenFileAtPage(fileId, pn, file.file_name)}
                    className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium transition-colors ${
                      fileId && onOpenFileAtPage
                        ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-200 dark:hover:bg-indigo-800/40 cursor-pointer'
                        : 'bg-gray-200 dark:bg-slate-700 text-gray-500 dark:text-slate-400'
                    }`}
                    title={`${file.file_name} — p. ${pn}`}
                    disabled={!fileId || !onOpenFileAtPage}
                  >
                    p. {pn}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function StatusBadge({ status }) {
  const colors = {
    compliant: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    non_compliant: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    partially_compliant: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    not_applicable: 'bg-gray-100 text-gray-500 dark:bg-slate-700 dark:text-slate-400',
  };
  if (!status) return null;
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${colors[status] || colors.not_applicable}`}>{status.replace(/_/g, ' ')}</span>;
}

export default function StudentEligibilityChecklist() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [student, setStudent] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [checklists, setChecklists] = useState([]);
  const [noData, setNoData] = useState(false);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [studentDisability, setStudentDisability] = useState(null);
  const [tabTemplateSections, setTabTemplateSections] = useState(null);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({ template_id: '', eligibility_date: '', disability_category: '' });
  const [syncing, setSyncing] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [animatedScore, setAnimatedScore] = useState(0);
  const [customTabs, setCustomTabs] = useState([]);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [addSearch, setAddSearch] = useState('');
  const [addingTemplate, setAddingTemplate] = useState(false);
  const [deletingTemplate, setDeletingTemplate] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [showStickyScore, setShowStickyScore] = useState(false);
  const [viewerFile, setViewerFile] = useState(null);
  const [viewerPage, setViewerPage] = useState(null);

  const addMenuRef = useRef(null);
  const addBtnRef = useRef(null);
  const progressBarRef = useRef(null);
  const saveTimerRef = useRef(null);
  const pendingChangesRef = useRef({});

  const fetchData = useCallback(async (checklistId) => {
    try {
      const [studentRes, checklistRes, listRes, templatesRes] = await Promise.allSettled([
        getStudent(id),
        getEligibilityChecklist(id, checklistId || undefined),
        listEligibilityChecklists(id),
        getTemplates(false, 1, 100),
      ]);
      if (studentRes.status === 'fulfilled') setStudent(studentRes.value.data.data);
      if (listRes.status === 'fulfilled') {
        setChecklists(coerceArray(listRes.value?.data?.data));
      }
      if (templatesRes.status === 'fulfilled') {
        const tplPayload = templatesRes.value?.data?.data;
        setTemplates(coerceArray(tplPayload?.templates ?? tplPayload));
      }
      if (checklistRes.status === 'fulfilled') {
        const data = checklistRes.value.data.data;
        if (data?.responses) {
          data.responses = normalizeChecklistResponses(data.responses);
        }
        setChecklist(data);
        setNoData(false);
        setTabTemplateSections(null);
        const dc = data?.disability_category || null;
        setStudentDisability(dc);
        setActiveTab(dc);
      } else {
        const status = checklistRes.reason?.response?.status;
        if (status === 404 || status === 422 || !checklistRes.reason?.response) {
          setNoData(true);
        } else {
          setAlert({ type: 'error', message: checklistRes.reason?.response?.data?.detail || 'Failed to load eligibility checklist.' });
        }
      }
    } catch {
      setAlert({ type: 'error', message: 'Failed to load data.' });
    } finally {
      setLoading(false);
    }
  }, [id]);

  const handleTabChange = useCallback(async (tabId) => {
    setActiveTab(tabId);
    setTabTemplateSections(null);

    const isStudentTab = tabId === studentDisability;
    const isMasterList = tabId === null;

    if (isStudentTab || isMasterList) {
      try {
        const res = await getEligibilityChecklist(id, checklist?.id, tabId || undefined);
        if (res.data?.data) {
          const tabData = res.data.data;
          if (tabData?.responses) {
            tabData.responses = normalizeChecklistResponses(tabData.responses);
          }
          setChecklist(tabData);
        }
      } catch {}
      return;
    }

    const customTab = customTabs.find(ct => ct.id === tabId);
    if (customTab?.templateId) {
      try {
        const res = await getTemplate(customTab.templateId);
        setTabTemplateSections(res.data.data?.sections || []);
      } catch {}
      return;
    }

    const matchingTemplate = coerceArray(templates).find(t => t.disability_category === tabId);
    if (matchingTemplate) {
      try {
        const res = await getTemplate(matchingTemplate.id);
        setTabTemplateSections(res.data.data?.sections || []);
      } catch {}
    }
  }, [id, checklist?.id, studentDisability, templates, customTabs]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const target = checklist?.completion_score ?? 0;
    setAnimatedScore(0);
    if (!target) return;
    const duration = 1000;
    const start = performance.now();
    let raf;
    const step = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setAnimatedScore(Math.round(eased * target));
      if (progress < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [checklist?.completion_score]);

  useEffect(() => {
    const el = progressBarRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setShowStickyScore(!entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [checklist]);

  const handleCreate = async () => {
    if (!createForm.template_id) {
      setAlert({ type: 'error', message: 'Please select a template.' });
      return;
    }
    setCreating(true);
    try {
      const body = { template_id: Number(createForm.template_id) };
      if (createForm.eligibility_date) body.eligibility_date = createForm.eligibility_date;
      if (createForm.disability_category) body.disability_category = createForm.disability_category;
      const res = await createEligibilityChecklist(id, body);
      setChecklist(res.data.data);
      setNoData(false);
      setAlert({ type: 'success', message: 'Eligibility checklist created.' });
      const listRes = await listEligibilityChecklists(id);
      setChecklists(listRes.data.data || []);
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Failed to create checklist.' });
    } finally {
      setCreating(false);
    }
  };

  const handleFieldChange = (fieldId, newValue) => {
    setChecklist(prev => ({
      ...prev,
      responses: { ...prev.responses, [fieldId]: newValue },
    }));
    pendingChangesRef.current[fieldId] = newValue;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      const changes = { ...pendingChangesRef.current };
      pendingChangesRef.current = {};
      try {
        await updateResponses(id, checklist.id, changes);
        const res = await getEligibilityChecklist(id, checklist.id);
        if (res.data?.data) {
          setChecklist(prev => ({
            ...prev,
            completion_score: res.data.data.completion_score,
            status: res.data.data.status,
          }));
        }
      } catch (err) {
        setAlert({ type: 'error', message: err.response?.data?.detail || 'Failed to save.' });
      }
    }, 800);
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await syncChecklist(id, checklist.id);
      setChecklist(res.data.data);
      setAlert({ type: 'success', message: res.data.message || 'Synced.' });
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Sync failed.' });
    } finally {
      setSyncing(false);
    }
  };

  const handleScanDocs = async () => {
    setScanning(true);
    try {
      const res = await evaluateFromDocs(id, checklist.id);
      setChecklist(res.data.data);
      setAlert({ type: 'success', message: res.data.message || 'Documents scanned.' });
    } catch (err) {
      if (err.response?.status === 400) setAlert({ type: 'error', message: 'Upload documents first before scanning.' });
      else setAlert({ type: 'error', message: err.response?.data?.detail || 'Scan failed.' });
    } finally {
      setScanning(false);
    }
  };

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const response = await exportPdf(id, checklist.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `eligibility_checklist_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Export failed.' });
    } finally {
      setExporting(false);
    }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const response = await exportExcel(id, checklist.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `eligibility_checklist_${id}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Export failed.' });
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteChecklist(id, checklist.id);
      setShowDeleteModal(false);
      setChecklist(null);
      setNoData(true);
      setAlert({ type: 'success', message: 'Checklist deleted.' });
      const listRes = await listEligibilityChecklists(id);
      setChecklists(listRes.data.data || []);
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Delete failed.' });
    } finally {
      setDeleting(false);
    }
  };

  const startEditing = () => { setEditData(deepClone(checklist)); setEditing(true); };

  const handleSaveHeader = async () => {
    setSaving(true);
    try {
      const res = await updateHeader(id, checklist.id, editData.header);
      setChecklist(res.data.data);
      setEditing(false);
      setEditData(null);
      setAlert({ type: 'success', message: 'Header updated.' });
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Save failed.' });
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (addMenuRef.current && addMenuRef.current.contains(e.target)) return;
      if (addBtnRef.current && addBtnRef.current.contains(e.target)) return;
      setAddMenuOpen(false);
      setAddSearch('');
    };
    if (addMenuOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [addMenuOpen]);

  const templateList = coerceArray(templates);
  const customTabList = coerceArray(customTabs);
  const allTabs = [
    ...TAB_ORDER,
    ...customTabList,
  ];

  const existingTabIds = new Set(allTabs.map(t => t.id));
  const availableTemplates = templateList.filter(t => {
    if (t.is_default) return false;
    const catId = t.disability_category || t.id?.toString();
    return !existingTabIds.has(catId);
  });
  const filteredAvailable = addSearch
    ? availableTemplates.filter(t => t.name.toLowerCase().includes(addSearch.toLowerCase()) || (t.disability_category || '').toLowerCase().includes(addSearch.toLowerCase()))
    : availableTemplates;

  const handleAddExistingTemplate = async (tmpl) => {
    setAddingTemplate(true);
    try {
      const tabId = tmpl.disability_category || `custom_${tmpl.id}`;
      const newTab = { id: tabId, label: tmpl.name, criteria: null, templateId: tmpl.id };
      setCustomTabs(prev => [...prev, newTab]);
      setAddMenuOpen(false);
      setAddSearch('');
      setActiveTab(tabId);

      const tmplRes = await getTemplate(tmpl.id);
      setTabTemplateSections(tmplRes.data.data?.sections || []);
      setAlert({ type: 'success', message: 'Template added.' });

      if (checklist?.id) {
        evaluateFromDocs(id, checklist.id)
          .then(() => getEligibilityChecklist(id, checklist.id))
          .then(res => { if (res.data?.data) setChecklist(res.data.data); })
          .catch(() => {});
      }
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Failed to add template.' });
    } finally {
      setAddingTemplate(false);
    }
  };

  const handleDeleteTemplate = async () => {
    if (!templateToDelete) return;
    setDeletingTemplate(true);
    try {
      await deleteTemplate(templateToDelete.templateId, true);
      setCustomTabs(prev => prev.filter(ct => ct.id !== templateToDelete.id));
      setTemplates(prev => coerceArray(prev).filter(t => t.id !== templateToDelete.templateId));
      if (activeTab === templateToDelete.id) {
        setActiveTab(null);
        setTabTemplateSections(null);
      }
      setTemplateToDelete(null);
      setAlert({ type: 'success', message: 'Template deleted successfully.' });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 400 && detail?.includes('Default')) {
        setAlert({ type: 'error', message: 'This template cannot be deleted.' });
      } else {
        setAlert({ type: 'error', message: detail || 'Failed to delete template.' });
      }
    } finally {
      setDeletingTemplate(false);
    }
  };

  const handleOpenFileAtPage = async (fileId, page, fileName) => {
    try {
      const { data } = await getFileView(id, fileId, page);
      const fileData = data.data || data;
      setViewerFile({
        file_name: fileData.file_name || fileName || 'Document',
        mime_type: fileData.mime_type || 'application/pdf',
        file_type: 'evaluation',
        student_id: id,
        id: fileData.student_file_id || fileId,
        student_file_id: fileData.student_file_id || fileId,
      });
      setViewerPage(fileData.page || page || null);
    } catch {
      setAlert({ type: 'error', message: 'Could not load file for viewing.' });
    }
  };

  if (loading) return <Loader fullScreen text="Loading eligibility checklist..." />;

  const data = checklist;
  const header = data?.header;
  const sectionDetails = data?.section_details || {};
  const score = data?.completion_score ?? 0;
  const scoreColor = score >= 70 ? 'bg-green-500' : score >= 30 ? 'bg-amber-500' : 'bg-red-500';

  const isStudentTab = activeTab === studentDisability;
  const isMasterList = activeTab === null;
  const isInactiveTab = !isStudentTab && !isMasterList;

  const sections = coerceArray(
    isInactiveTab && tabTemplateSections
      ? tabTemplateSections
      : isInactiveTab
        ? []
        : (data?.template_snapshot || []),
  );
  const responses = isInactiveTab ? {} : (data?.responses || {});

  // Render a timeline section as a table: # | Task | Sent/Complete
  const renderTimelineSection = (section) => {
    const fields = [...(section.fields || [])].sort((a, b) => a.order - b.order);
    return (
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <th className={HCELL} style={{ width: '30px' }}>#</th>
            <th className={HCELL}>Task</th>
            <th className={HCELL} style={{ width: '90px' }}>Sent/Complete</th>
          </tr>
        </thead>
        <tbody>
          {fields.map((field, idx) => {
            const resp = responses[field.id] || {};
            return (
              <tr key={field.id}>
                <td className={`${CELL} text-center text-xs`}>{idx + 1}</td>
                <td className={CELL}>
                  <span className="text-sm">{field.label}</span>
                  <NotesHint notes={resp.notes} />
                </td>
                <td className={`${CELL} text-center`}>
                  <input
                    type="checkbox"
                    checked={coerceSentComplete(resp.sent_complete)}
                    onChange={e => handleFieldChange(field.id, { ...resp, sent_complete: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-green-600 focus:ring-green-500"
                    disabled={isInactiveTab}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  // Render a yes/no field section as a table
  const renderFieldSection = (section) => {
    const fields = [...(section.fields || [])].sort((a, b) => a.order - b.order);
    const editable = !isInactiveTab;
    return (
      <table className="w-full border-collapse">
        <tbody>
          {fields.map(field => {
            const resp = responses[field.id] || {};
            const val = resp.value;
            return (
              <tr key={field.id} className={isInactiveTab ? 'opacity-50' : ''}>
                <td className={`${CELL} w-8 text-center`}>
                  {field.type === 'yes_no' && <CB checked={val} />}
                  {field.type === 'yes_no_na' && <CB checked={val} />}
                  {field.type === 'text' && <span className="text-slate-300">✎</span>}
                  {field.type === 'date' && <span className="text-slate-300">📅</span>}
                </td>
                <td className={CELL}>
                  <span className="text-sm">{field.label}</span>
                  {field.help_text && <span className="text-[10px] text-gray-400 ml-1">({field.help_text})</span>}
                  <NotesHint notes={resp.notes} />
                </td>
                <td className={`${CELL} w-[120px] sm:w-[200px]`}>
                  {field.type === 'yes_no' && (
                    <YesNoCell value={val} editing={editable} onChange={v => handleFieldChange(field.id, { ...resp, value: v })} />
                  )}
                  {field.type === 'yes_no_na' && (
                    <YesNoNaCell value={val} editing={editable} onChange={v => handleFieldChange(field.id, { ...resp, value: v })} />
                  )}
                  {field.type === 'text' && (
                    <input
                      type="text"
                      value={Array.isArray(resp.value) ? resp.value.join(', ') : (resp.value || '')}
                      onChange={e => handleFieldChange(field.id, { ...resp, value: e.target.value })}
                      className="w-full px-2 py-1 text-xs rounded border border-gray-200 dark:border-slate-600 bg-transparent text-gray-900 dark:text-white"
                      placeholder="Enter text..."
                      disabled={!editable}
                    />
                  )}
                  {field.type === 'date' && (
                    <DatePicker
                      value={resp.value || ''}
                      onChange={(v) => handleFieldChange(field.id, { ...resp, value: v })}
                      className="w-full px-2 py-1 text-xs rounded border border-gray-200 dark:border-slate-600 bg-transparent text-gray-900 dark:text-white"
                      disabled={!editable}
                      aria-label={field.label || 'Date'}
                    />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  return (
    <div>
      {alert && (
        <div className="fixed top-20 right-4 z-50 w-80 sm:w-96">
          <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />
        </div>
      )}

      <ConfirmModal
        open={showDeleteModal}
        title="Delete Eligibility Checklist"
        message="This will permanently delete this checklist. This action cannot be undone."
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteModal(false)}
        confirmText="Delete"
        variant="danger"
        loading={deleting}
      />

      <ConfirmModal
        open={!!templateToDelete}
        title="Delete Template"
        message={`Are you sure you want to permanently delete the template '${templateToDelete?.label || ''}'? This cannot be undone.`}
        onConfirm={handleDeleteTemplate}
        onCancel={() => setTemplateToDelete(null)}
        confirmText="Delete"
        variant="danger"
        loading={deletingTemplate}
      />

      {/* Page header */}
      <div className="px-3 sm:px-6 pt-3 sm:pt-6 mb-4 sm:mb-5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <button onClick={() => navigate(`/students/${id}/information`)} className="p-1.5 sm:p-2 rounded-xl text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors flex-shrink-0">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            </button>
            <div className="min-w-0">
              <h1 className="text-sm sm:text-lg font-bold text-gray-900 dark:text-white truncate">Eligibility Compliance Checklist</h1>
              {student && <p className="text-[10px] sm:text-xs text-gray-500 dark:text-slate-400 truncate">{student.student_name}</p>}
            </div>
          </div>
          {checklist && !noData && (
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0 flex-wrap justify-end">
              <button onClick={handleScanDocs} disabled={scanning} title="Scan Documents with AI" className="inline-flex items-center gap-1.5 p-1.5 sm:px-3 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold bg-purple-600 text-white hover:bg-purple-700 transition-colors disabled:opacity-50">
                {scanning ? (
                  <><svg className="animate-spin w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" /></svg><span className="hidden md:inline">Scanning...</span></>
                ) : (
                  <><svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg><span className="hidden md:inline">Scan Docs</span></>
                )}
              </button>
              <button onClick={handleSync} disabled={syncing} title="Sync from Compliance" className="inline-flex items-center gap-1.5 p-1.5 sm:px-3 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50">
                {syncing ? <svg className="animate-spin w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" /></svg> : <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>}
                <span className="hidden md:inline">{syncing ? 'Syncing...' : 'Sync'}</span>
              </button>
              <button onClick={handleExportPdf} disabled={exporting} title="Export PDF" className="inline-flex items-center gap-1.5 p-1.5 sm:px-3 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors disabled:opacity-50">
                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                <span className="hidden md:inline">PDF</span>
              </button>
              <button onClick={handleExportExcel} disabled={exporting} title="Export Excel" className="inline-flex items-center gap-1.5 p-1.5 sm:px-3 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold bg-green-600 text-white hover:bg-green-700 transition-colors disabled:opacity-50">
                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                <span className="hidden md:inline">Excel</span>
              </button>
              <button onClick={editing ? handleSaveHeader : startEditing} disabled={editing && saving} title={editing ? 'Save' : 'Edit'} className={`inline-flex items-center gap-1.5 p-1.5 sm:px-3 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold transition-colors disabled:opacity-50 ${editing ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-amber-500 text-white hover:bg-amber-600'}`}>
                {editing ? (
                  saving ? (
                    <svg className="animate-spin w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" /></svg>
                  ) : (
                    <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  )
                ) : (
                  <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                )}
                <span className="hidden md:inline">{editing ? (saving ? 'Saving...' : 'Save') : 'Edit'}</span>
              </button>
              <button onClick={() => setShowDeleteModal(true)} title="Delete" className="inline-flex items-center gap-1.5 p-1.5 sm:px-3 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold bg-red-600 text-white hover:bg-red-700 transition-colors">
                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                <span className="hidden md:inline">Delete</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Sticky score badge */}
      {showStickyScore && checklist && (
        <div className="fixed top-14 sm:top-16 right-3 sm:right-6 z-50 flex items-center gap-2 sm:gap-2.5 bg-white/90 dark:bg-slate-800/90 backdrop-blur-md border border-gray-200 dark:border-slate-700 rounded-full px-3 sm:px-4 py-1.5 sm:py-2 shadow-lg transition-all">
          <div className="w-16 sm:w-24 h-1.5 sm:h-2 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-500 ${scoreColor}`} style={{ width: `${animatedScore}%` }} />
          </div>
          <span className="text-[10px] sm:text-xs font-bold text-gray-700 dark:text-slate-200">{animatedScore}%</span>
        </div>
      )}

      {/* Content */}
      <div className="px-3 sm:px-6 pb-6">
        {/* Empty State */}
        {noData && !checklist && (
          <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-2xl p-6 sm:p-10">
            <div className="max-w-md mx-auto text-center">
              <div className="w-16 h-16 rounded-2xl bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center mx-auto mb-5">
                <svg className="w-8 h-8 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Create Eligibility Checklist</h3>
              <p className="text-gray-500 dark:text-slate-400 text-sm mb-6">Select a template to begin.</p>
              <div className="space-y-4 text-left">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Template *</label>
                  <select value={createForm.template_id} onChange={e => setCreateForm(p => ({ ...p, template_id: e.target.value }))} className="w-full px-3 py-2.5 rounded-xl border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm">
                    <option value="">Select template...</option>
                    {templateList.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Eligibility Date</label>
                  <DatePicker
                    value={createForm.eligibility_date}
                    onChange={(v) => setCreateForm((p) => ({ ...p, eligibility_date: v }))}
                    className="w-full px-3 py-2.5 rounded-xl border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm"
                    aria-label="Eligibility date"
                  />
                </div>
                <button onClick={handleCreate} disabled={creating} className="w-full py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold disabled:opacity-50">
                  {creating ? 'Creating...' : 'Create Checklist'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Cycle selector if multiple */}
        {checklist && coerceArray(checklists).length > 1 && (
          <div className="mb-3 flex items-center gap-3">
            <label className="text-xs font-medium text-gray-500 dark:text-slate-400">Cycle:</label>
            <select
              value={checklist.id}
              onChange={e => { setLoading(true); fetchData(Number(e.target.value)); }}
              className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm text-gray-900 dark:text-white"
            >
              {coerceArray(checklists).map(c => (
                <option key={c.id} value={c.id}>{c.eligibility_date || `#${c.id}`} — {c.disability_category || 'General'} ({c.completion_score}%)</option>
              ))}
            </select>
          </div>
        )}

        {/* Checklist Document */}
        {checklist && (
          <div className="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-none sm:rounded-lg shadow-sm overflow-x-auto -mx-3 sm:mx-0">
            <div className="min-w-0 sm:min-w-[600px]">

              {/* Header metadata — stacks on mobile */}
              <div className="grid grid-cols-1 sm:grid-cols-2">
                <div className={`${CELL} font-medium`}>
                  <span className="text-xs text-slate-500 dark:text-slate-400">Student Name</span><br />
                  {editing ? (
                    <input value={editData?.header?.student_name || ''} onChange={e => setEditData(p => ({ ...p, header: { ...p.header, student_name: e.target.value } }))} className="px-2 py-0.5 rounded border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm mt-0.5 w-full" />
                  ) : (
                    <span className="text-sm sm:text-base font-semibold">{header?.student_name || student?.student_name || '—'}</span>
                  )}
                </div>
                <div className={`${CELL}`}>
                  <span className="text-xs text-slate-500 dark:text-slate-400">Eligibility Due</span><br />
                  {editing ? (
                    <DatePicker
                      value={editData?.header?.eligibility_date || ''}
                      onChange={(v) => setEditData((p) => ({ ...p, header: { ...p.header, eligibility_date: v } }))}
                      className="px-2 py-0.5 rounded border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm mt-0.5 w-full sm:w-auto"
                      aria-label="Eligibility date"
                    />
                  ) : (
                    <span className="font-medium">{formatChecklistDate(header?.eligibility_date)}</span>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2">
                <div className={`${CELL}`}>
                  <span className="text-xs text-slate-500 dark:text-slate-400">DOB:</span>{' '}
                  <span className="text-sm">{resolveChecklistDob(header, student, sectionDetails)}</span>
                  <span className="ml-4 text-xs text-slate-500 dark:text-slate-400">Grade:</span>{' '}
                  <span className="text-sm">{header?.grade || '—'}</span>
                </div>
                <div className={`${CELL}`}>
                  <span className="text-xs text-slate-500 dark:text-slate-400">Category:</span>{' '}
                  <span className="text-sm font-medium capitalize">{(data?.disability_category || '—').replace(/_/g, ' ')}</span>
                  <span className="ml-2 sm:ml-3">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${data?.status === 'complete' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'}`}>
                      {data?.status === 'complete' ? 'Complete' : 'In Progress'}
                    </span>
                  </span>
                </div>
              </div>
              <div ref={progressBarRef} className={`${CELL}`}>
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="flex-1 h-2 sm:h-2.5 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                    <div className={`h-full rounded-full transition-all duration-500 ${scoreColor}`} style={{ width: `${animatedScore}%` }} />
                  </div>
                  <span className="text-[10px] sm:text-xs font-bold text-gray-700 dark:text-slate-200 w-10 text-right">{animatedScore}%</span>
                </div>
                <p className="text-[10px] text-gray-400 mt-1">** type N/A in complete for any areas that do not apply to student</p>
              </div>

              {/* Inactive tab banner */}
              {isInactiveTab && (
                <div className="px-4 py-3 bg-gray-50 dark:bg-slate-700/30 border-b border-slate-200 dark:border-slate-700">
                  <p className="text-xs text-gray-500 dark:text-slate-400 italic text-center">No eligibility data for this category — showing empty template structure</p>
                </div>
              )}

              {/* Sections — filtered by active tab */}
              {sections.sort((a, b) => a.order - b.order)
                .filter(section => {
                  if (activeTab === null) return true;
                  const tab = allTabs.find(t => t.id === activeTab);
                  if (!tab) return true;
                  const isCustom = customTabs.some(ct => ct.id === activeTab);
                  if (isCustom) return true;
                  const allowedIds = ['timeline', 'pre_meeting', 'post_meeting'];
                  if (tab?.criteria) allowedIds.push(tab.criteria);
                  return allowedIds.includes(section.id);
                })
                .map(section => {
                const detail = sectionDetails[section.id];
                const isTimeline = section.id === 'timeline';
                const isDisabilitySection = section.id.endsWith('_criteria');

                return (
                  <div key={section.id}>
                    <table className="w-full border-collapse">
                      <tbody>
                        <tr>
                          <td colSpan={5} className={`${SECTION_TITLE} ${isDisabilitySection ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-800 dark:text-purple-300' : ''}`}>
                            <div className="flex items-center justify-between">
                              <ChecklistSectionPopover
                                sectionKeys={complianceKeysForEligibilitySection(section.id)}
                                sectionDetails={sectionDetails}
                                onOpenFileAtPage={handleOpenFileAtPage}
                              >
                                <span>{isDisabilitySection ? `THIS SECTION IS FOR: ${section.name.toUpperCase()}` : section.name}</span>
                              </ChecklistSectionPopover>
                              <div className="flex items-center gap-2">
                                {detail && <StatusBadge status={detail.status} />}
                              </div>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    {isTimeline ? renderTimelineSection(section) : renderFieldSection(section)}
                    {detail && <SourcePanel sectionDetail={detail} onOpenFileAtPage={handleOpenFileAtPage} />}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Bottom tab bar (Excel sheet tabs) */}
      {checklist && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white dark:bg-slate-900 border-t border-gray-300 dark:border-slate-700 shadow-lg safe-area-bottom">
          <div className="flex items-center overflow-x-auto px-1 sm:px-2 py-0.5 sm:py-1 gap-0 scrollbar-hide">
            {allTabs.map(tab => {
              const isActive = activeTab === tab.id;
              const hasData = tab.id === studentDisability || tab.id === null;
              const isCustom = customTabs.some(ct => ct.id === tab.id);
              return (
                <span
                  key={tab.id || 'master'}
                  className={`flex-shrink-0 inline-flex items-center border-t-2 transition-colors ${
                    isActive
                      ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300'
                      : hasData
                        ? 'border-transparent text-gray-600 dark:text-slate-300 hover:text-gray-800 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-slate-800'
                        : 'border-transparent text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-800'
                  }`}
                >
                  <button
                    onClick={() => handleTabChange(tab.id)}
                    className="px-2 sm:px-3 py-1 sm:py-1.5 text-[10px] sm:text-xs font-medium whitespace-nowrap"
                  >
                    {tab.label}
                    {tab.id === studentDisability && <span className="ml-1 w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />}
                  </button>
                  {isCustom && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setTemplateToDelete(tab); }}
                      title={`Delete ${tab.label}`}
                      className="pr-1 sm:pr-1.5 -ml-1 text-[12px] sm:text-[14px] leading-none text-[#999] hover:text-[#e53e3e] transition-colors"
                    >
                      ×
                    </button>
                  )}
                </span>
              );
            })}

            {/* Add Template "+" button */}
            <button
              ref={addBtnRef}
              onClick={() => { setAddMenuOpen(v => !v); setAddSearch(''); }}
              disabled={addingTemplate}
              title="Add Template"
              className="ml-1 sm:ml-1.5 w-6 h-6 sm:w-7 sm:h-7 flex-shrink-0 flex items-center justify-center rounded-full border border-dashed border-gray-400 dark:border-slate-500 text-gray-500 dark:text-slate-400 text-xs sm:text-sm font-bold hover:border-solid hover:border-gray-500 dark:hover:border-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-700 dark:hover:text-white transition-all disabled:opacity-50"
            >
              {addingTemplate ? (
                <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" /></svg>
              ) : '+'}
            </button>
          </div>

          {/* Add Template dropdown — rendered outside the overflow container */}
          {addMenuOpen && (
            <div ref={addMenuRef} className="absolute bottom-full mb-1 right-1 sm:right-2 w-60 sm:w-72 bg-[#1a202c] border border-[#2d3748] rounded-lg shadow-2xl z-50">
              <div className="p-2 border-b border-[#2d3748]">
                <input
                  type="text"
                  value={addSearch}
                  onChange={e => setAddSearch(e.target.value)}
                  placeholder="Search templates..."
                  className="w-full px-3 py-2 text-xs rounded-md border border-[#2d3748] bg-[#0f172a] text-white placeholder-slate-500 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  autoFocus
                />
              </div>
              <div className="max-h-[240px] overflow-y-auto">
                {filteredAvailable.length === 0 ? (
                  <p className="text-xs text-slate-400 text-center py-4">All templates are already added.</p>
                ) : filteredAvailable.map(tmpl => (
                  <button
                    key={tmpl.id}
                    onClick={() => handleAddExistingTemplate(tmpl)}
                    disabled={addingTemplate}
                    className="w-full text-left px-3 py-2 text-white hover:bg-[#2d3748] transition-colors disabled:opacity-50"
                  >
                    <span className="block font-medium text-xs">{tmpl.name}</span>
                    {tmpl.disability_category && (
                      <span className="block text-[10px] text-slate-400 mt-0.5">{tmpl.disability_category.replace(/_/g, ' ')}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Bottom padding for tab bar */}
      {checklist && <div className="h-10 sm:h-10" />}

      <DocumentViewer
        open={!!viewerFile}
        file={viewerFile}
        page={viewerPage}
        studentId={id}
        onClose={() => { setViewerFile(null); setViewerPage(null); }}
      />
    </div>
  );
}

